import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[var(--text-secondary)]">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleLogout = () => {
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
    logout();
    navigate('/login');
  };

  const serverDetails = {
    url: 'https://remote.ivalueinfosol.net/guacamole/#/client/NDMAYwBteXNxbA',
    username: 'winuser',
    password: 'iValue2025$',
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Dashboard Header */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[var(--bg-primary)]/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              
              <div>
                <h1 className="text-xl font-semibold" style={{ fontFamily: "var(--font-display)" }}>
                  Dashboard
                </h1>
                <p className="text-xs text-[var(--text-secondary)]">Identity Security Management</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={user.picture}
                  alt={user.name}
                  className="h-10 w-10 rounded-full ring-2 ring-white/10"
                />
                <div className="hidden sm:block">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-[var(--text-secondary)]">{user.email}</div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition-colors hover:bg-white/[0.06] hover:text-[var(--text-primary)]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-6">
          {/* Left Sidebar */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 space-y-2 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-4">
              <h2 className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                Navigation
              </h2>
              <nav className="space-y-1">
                {[
                  { label: 'Overview', icon: '📊', active: true }
                ].map((item) => (
                  <button
                    key={item.label}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${
                      item.active
                        ? 'bg-white/[0.06] text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)] hover:bg-white/[0.04] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <span className="text-base">{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>

             
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 space-y-6">
            {/* Welcome Section */}
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-xl p-6">
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "var(--font-display)" }}>
                Welcome back, {user.name.split(' ')[0]}! 👋
              </h2>
              <p className="text-[var(--text-secondary)]">
                Here's what's happening with your identity security infrastructure today.
              </p>
            </div>

            {/* Server Access Details */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 text-2xl">
                  🖥️
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ fontFamily: "var(--font-display)" }}>
                    Remote Server Access
                  </h3>
                  <p className="text-xs text-[var(--text-secondary)]">Guacamole Remote Desktop</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Server URL */}
                <div className="rounded-xl bg-white/[0.02] p-4">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-2">
                    Server URL
                  </label>
                  <div className="flex items-center gap-2">
                    <a
                      href={serverDetails.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-sm text-[var(--accent-trust)] hover:text-[var(--accent-cyan)] transition-colors break-all"
                    >
                      {serverDetails.url}
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(serverDetails.url);
                      }}
                      className="shrink-0 rounded-lg bg-white/[0.05] px-3 py-1.5 text-xs hover:bg-white/[0.08] transition-colors"
                      title="Copy URL"
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>

                {/* Credentials */}
                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Username */}
                  <div className="rounded-xl bg-white/[0.02] p-4">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-2">
                      Username
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono text-[var(--text-primary)] bg-black/20 px-3 py-2 rounded-lg">
                        {serverDetails.username}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(serverDetails.username);
                        }}
                        className="shrink-0 rounded-lg bg-white/[0.05] px-3 py-1.5 text-xs hover:bg-white/[0.08] transition-colors"
                        title="Copy username"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  {/* Password */}
                  <div className="rounded-xl bg-white/[0.02] p-4">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-2">
                      Password
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono text-[var(--text-primary)] bg-black/20 px-3 py-2 rounded-lg">
                        {serverDetails.password}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(serverDetails.password);
                        }}
                        className="shrink-0 rounded-lg bg-white/[0.05] px-3 py-1.5 text-xs hover:bg-white/[0.08] transition-colors"
                        title="Copy password"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                </div>

                {/* Quick Access Button */}
                <div className="pt-2">
                  <a
                    href={serverDetails.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent-trust)] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-transform hover:translate-y-[-1px]"
                  >
                    Launch Remote Desktop
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
